import { useRef, useCallback } from 'react';
import { arrayMove } from '@dnd-kit/sortable';
import { Box, Text } from '@nimbus-ds/components';
import { noop } from 'lodash';
import { CategoryTabedResponseDto } from '@tiendanube/common';
import { EmptySearch, InterfaceNameValue, Stack } from '@tiendanube/components';
import {
  CardWithTitle,
  ErrorScreen,
  SortableDataList,
  DraggableDataListNew,
} from 'commons/components';
import { spliceImmutable } from 'commons/utils';
import useTranslationCatalog from 'domains/Catalog/useTranslationCatalog';
import { SectionProductItemInterface, SortBySelectType } from 'domains/Online';
import {
  CategoriesSelect,
  ProductsBySectionItem,
  SortBySelect,
  SelectedProductsActionBar,
  ActionSelectedParams,
  Action,
} from './components';

import './ProductsBySection.scss';

interface ProductsBySectionProps {
  products: SectionProductItemInterface[];
  productsStatus: string;
  productsCount: number;
  categoryId: string;
  categories: CategoryTabedResponseDto[];
  sortBy: SortBySelectType;
  isLoading: boolean;
  isLoadingRemainingProducts: boolean;
  onEnded: () => void;
  onChange: (data: InterfaceNameValue) => void;
  onChangeProducts: (products: SectionProductItemInterface[]) => void;
  onFetchRemainingProducts: (params: {
    categoryId: string;
    sortBy: SortBySelectType;
    productId: string;
  }) => Promise<SectionProductItemInterface[]>;
}

function ProductsBySection({
  products,
  productsStatus,
  productsCount,
  categoryId,
  categories,
  sortBy,
  isLoading,
  isLoadingRemainingProducts,
  onEnded,
  onChange,
  onChangeProducts,
  onFetchRemainingProducts,
}: Readonly<ProductsBySectionProps>) {
  const t = useTranslationCatalog();
  const shouldUseVirtualizedList = productsCount > 2000;
  const containerRef = useRef<HTMLDivElement>(null);

  const handleChangeProducts = (products) => onChangeProducts(products);

  const handleChangeSelected = (position: number, checked: boolean) => {
    onChangeProducts(
      products.map((product, index) => ({
        ...product,
        selected: index === position ? checked : product.selected,
      })),
    );
  };

  const anySelected = products.some((product) => product.selected);

  const count = products.filter((product) => product.selected).length;

  const clearSelectedProducts = (products: SectionProductItemInterface[]) =>
    products.map((product) => ({
      ...product,
      selected: false,
    }));

  const handleActionSelected = async (params: ActionSelectedParams) => {
    const { action } = params;

    // For BOTTOM and SPECIFIC actions, fetch remaining products if needed
    const shouldFetchRemaining =
      sortBy === 'user' &&
      ((action === Action.BOTTOM && products.length < productsCount) ||
        (action === Action.SPECIFIC && params.position > products.length));

    // Get the current products to work with
    let currentProducts = products;

    if (shouldFetchRemaining) {
      try {
        const lastProduct = products[products.length - 1];
        currentProducts = await onFetchRemainingProducts({
          categoryId,
          sortBy,
          productId: lastProduct.productId,
        });
      } catch (error) {
        return;
      }
    }

    // Execute the action with current products state
    executeAction(params, currentProducts);
  };

  // Separate function to execute the actual action logic
  const executeAction = useCallback(
    (
      params: ActionSelectedParams,
      currentProducts: SectionProductItemInterface[],
    ) => {
      const { action } = params;
      const selectedProducts = currentProducts.filter(
        (product) => product.selected,
      );
      const unselectedProducts = currentProducts.filter(
        (product) => !product.selected,
      );
      let newProducts: SectionProductItemInterface[] = [];

      switch (action) {
        case Action.TOP: {
          newProducts = [...selectedProducts, ...unselectedProducts];
          break;
        }
        case Action.BOTTOM: {
          newProducts = [...unselectedProducts, ...selectedProducts];
          break;
        }
        case Action.SPECIFIC: {
          const { position } = params;
          newProducts = spliceImmutable(
            unselectedProducts,
            position - 1,
            0,
            ...selectedProducts,
          );
          break;
        }
      }
      onChangeProducts(clearSelectedProducts(newProducts));
    },
    [onChangeProducts],
  );

  const handleMoveProductToUp = (productIndex: number) => {
    if (!productIndex) return;

    // prevent scroll to top just keep current scroll position
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const handleScroll = () => window.scrollTo(0, scrollTop);
    window.addEventListener('scroll', handleScroll);

    onChangeProducts(arrayMove(products, productIndex, 0));

    setTimeout(() => {
      window.removeEventListener('scroll', handleScroll);
    }, 100);
  };

  const isUserSort = sortBy === 'user';

  const renderProductItem = (
    product: SectionProductItemInterface,
    index: number,
  ) => (
    <ProductsBySectionItem
      productId={product.productId}
      name={product.name}
      imageUrl={product.imageUrl}
      visible={product.visible}
      stock={product.stock}
      position={index}
      selected={isUserSort ? product.selected : undefined}
      onChangeSelected={isUserSort ? handleChangeSelected : undefined}
      onMoveProductToUp={isUserSort ? handleMoveProductToUp : undefined}
    />
  );

  return (
    <Box data-style="stratus--organize-products-page">
      <CardWithTitle title={t('products.order.productsBySeccion.title')}>
        <Stack column align="stretch">
          <Text>{t('products.order.productsBySeccion.subtitle')}</Text>
          {count === 0 && (
            <Box
              display="grid"
              gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}
              gap="2"
            >
              <CategoriesSelect
                categories={categories}
                value={categoryId}
                onChange={onChange}
              />
              <SortBySelect value={sortBy} onChange={onChange} />
            </Box>
          )}
          <div className="stratus--margins-expand" ref={containerRef}>
            <SelectedProductsActionBar
              count={count}
              productsCount={productsCount}
              onActionSelected={handleActionSelected}
              parentRef={containerRef}
              disabled={isLoading || isLoadingRemainingProducts}
              onUnselectAll={() => {
                onChangeProducts(clearSelectedProducts(products));
              }}
            />
            {productsStatus === 'success' && products.length === 0 && (
              <EmptySearch
                title={t('products.order.productsBySeccion.emptySearch.title')}
                text={t('products.order.productsBySeccion.emptySearch.text')}
              />
            )}
            {productsStatus === 'error' && (
              <ErrorScreen
                onRetry={onEnded}
                description={t('products.order.productsBySeccion.error')}
              />
            )}
            {productsStatus !== 'error' &&
              (shouldUseVirtualizedList ? (
                <div
                  style={{
                    pointerEvents: isLoadingRemainingProducts ? 'none' : 'auto',
                    animation: isLoadingRemainingProducts
                      ? 'stratus--loading-pulse 0.5s ease-in-out infinite alternate'
                      : undefined,
                  }}
                >
                  <DraggableDataListNew<SectionProductItemInterface>
                    data={products}
                    onChange={
                      isLoadingRemainingProducts ? noop : handleChangeProducts
                    }
                    showFooter={productsStatus === 'loading'}
                    endReached={onEnded}
                    renderItem={(product, index) =>
                      renderProductItem(product, index)
                    }
                    disabled={anySelected}
                  />
                </div>
              ) : (
                <div
                  style={{
                    pointerEvents: isLoadingRemainingProducts ? 'none' : 'auto',
                    animation: isLoadingRemainingProducts
                      ? 'stratus--loading-pulse 1.5s ease-in-out infinite alternate'
                      : undefined,
                  }}
                >
                  <SortableDataList<SectionProductItemInterface>
                    customIdKey="productId"
                    items={products}
                    onChange={
                      isLoadingRemainingProducts ? noop : handleChangeProducts
                    }
                    showFooter={productsStatus === 'loading'}
                    endReached={onEnded}
                    renderItem={(product, index) =>
                      renderProductItem(product, index)
                    }
                    disabled={anySelected}
                  />
                </div>
              ))}
          </div>
        </Stack>
      </CardWithTitle>
    </Box>
  );
}

export default ProductsBySection;
